// Session: PIN setup, lock/unlock, auto-lock, parent name, wipe-all.
//
// Threat model: a phone whose screen lock is bypassed (lost, lent, borrowed by
// a child). The PIN is a second factor over the OS lock. Encryption-at-rest
// (handled by the backend, keyed off our session key) protects the data even
// against backup extraction.
//
// Forgetting the PIN means the data cannot be recovered — there is no
// recovery code. This is by design: medical data, single device, two users
// who share a memorable PIN. The user is warned of this on first PIN setup.

import {
  generateSalt, deriveKey, makeVerifier, checkVerifier,
} from '../crypto/crypto.js';
import {
  setSessionKey, clearSessionKey, hasSessionKey,
  getConfig, setConfig,
  migrateAllToEncrypted, wipeAll as backendWipe,
} from '../storage/storage.js';

const CONFIG = {
  pinSalt:     'pin.salt',
  pinVerifier: 'pin.verifier',
  parentName:  'parent.firstName',
  autoLockMs:  'session.autoLockMs',
};

export const DEFAULT_AUTO_LOCK_MS = 60_000;

const listeners = { unlock: new Set(), lock: new Set() };
let backgroundedAt = null;
let bgTimer = null;

export function isUnlocked() { return hasSessionKey(); }

export async function hasPin() {
  return !!(await getConfig(CONFIG.pinSalt));
}

/** First-run PIN setup. Generates salt, derives key, stores verifier, migrates any pre-existing data. */
export async function setupPin(pin) {
  validatePin(pin);
  if (await hasPin()) throw new Error('PIN already set — use change-PIN instead.');

  const salt = generateSalt();
  const key = await deriveKey(pin, salt);
  const verifier = await makeVerifier(key);

  await setConfig(CONFIG.pinSalt, salt);
  await setConfig(CONFIG.pinVerifier, verifier);

  setSessionKey(key);
  // Re-encrypt anything captured before PIN setup (test photos etc.).
  await migrateAllToEncrypted();
  emit('unlock');
}

/** Try to unlock with a PIN. Returns true on success, false on wrong PIN. */
export async function unlock(pin) {
  const salt = await getConfig(CONFIG.pinSalt);
  const verifier = await getConfig(CONFIG.pinVerifier);
  if (!salt || !verifier) throw new Error('No PIN configured');

  const key = await deriveKey(pin, salt);
  if (!(await checkVerifier(key, verifier))) return false;

  setSessionKey(key);
  emit('unlock');
  return true;
}

export function lock() {
  if (!isUnlocked()) return;
  clearSessionKey();
  emit('lock');
}

/** Subscribe to lock/unlock. Returns an unsubscribe function. */
export function on(event, fn) {
  listeners[event]?.add(fn);
  return () => listeners[event]?.delete(fn);
}

function emit(event) {
  for (const fn of listeners[event] ?? []) {
    try { fn(); } catch (e) { console.warn(`Listener for ${event} failed:`, e); }
  }
}

// ---- Settings (parent name, auto-lock) ----

export async function getParentName() {
  return (await getConfig(CONFIG.parentName)) ?? null;
}

export async function setParentName(name) {
  if (!name) {
    await setConfig(CONFIG.parentName, null);
    return null;
  }
  // First name only, max 24 chars. Avoid accidental full-name storage.
  const clean = String(name).trim().split(/\s+/)[0].slice(0, 24);
  await setConfig(CONFIG.parentName, clean);
  return clean;
}

export async function getAutoLockMs() {
  return (await getConfig(CONFIG.autoLockMs)) ?? DEFAULT_AUTO_LOCK_MS;
}

export async function setAutoLockMs(ms) {
  await setConfig(CONFIG.autoLockMs, ms);
}

// ---- Auto-lock on backgrounding ----

export function startAutoLock() {
  document.addEventListener('visibilitychange', onVisibilityChange);
}

function isAutoLockDisabled(ms) {
  return ms == null || ms < 0;
}

async function onVisibilityChange() {
  if (!isUnlocked()) return;
  const ms = await getAutoLockMs();

  if (document.visibilityState === 'hidden') {
    backgroundedAt = Date.now();
    if (bgTimer) { clearTimeout(bgTimer); bgTimer = null; }
    if (isAutoLockDisabled(ms)) return;
    // Background timers are throttled on Android — this is a best-effort fallback.
    // The visible-side check below is the reliable lock trigger.
    bgTimer = setTimeout(() => lock(), ms);
  } else {
    if (bgTimer) { clearTimeout(bgTimer); bgTimer = null; }
    if (backgroundedAt && !isAutoLockDisabled(ms)) {
      if (Date.now() - backgroundedAt >= ms) lock();
    }
    backgroundedAt = null;
  }
}

// ---- Wipe-all ----

export async function wipeAllData() {
  await backendWipe();
  emit('lock');
}

// ---- Validation ----

function validatePin(pin) {
  if (typeof pin !== 'string') throw new Error('PIN must be a string');
  if (pin.length < 4) throw new Error('PIN must be at least 4 digits');
  if (pin.length > 16) throw new Error('PIN must be 16 characters or fewer');
}
